<template>
  <div class="d-flex flex-column">
    <Transition name="bulk-bar">
      <div v-if="count > 0" class="bulk-bar" data-cy="bulk-toolbar">
        <div class="bulk-bar__info">
          <span class="bulk-bar__check" aria-hidden="true">
            <i class="fa-solid fa-check"></i>
          </span>
          <span class="bulk-bar__count" data-cy="bulk-selected-count">
            {{ count }} selected
          </span>
          <span class="bulk-bar__divider" aria-hidden="true"></span>
          <button
            type="button"
            class="bulk-bar__link"
            data-cy="bulk-select-all"
            @click="selectAll"
          >
            Select all
          </button>
          <button
            type="button"
            class="bulk-bar__link"
            data-cy="bulk-clear"
            @click="clear"
          >
            Clear
          </button>
        </div>
        <div class="bulk-bar__actions">
          <SeraButton
            type="secondary"
            icon="fa-regular fa-eye-slash"
            class="w-full !px-4 !py-2 text-sm font-medium sm:w-auto sm:!min-w-[170px]"
            data-cy="bulk-hide"
            @click="onApply(false)"
          >
            Hide from customer
          </SeraButton>
          <SeraButton
            icon="fa-regular fa-eye"
            class="w-full !px-4 !py-2 text-sm font-medium sm:w-auto sm:!min-w-[170px]"
            data-cy="bulk-show"
            @click="onApply(true)"
          >
            Show to customer
          </SeraButton>
        </div>
      </div>
    </Transition>
    <div class="mb-8">
      <Photos
        :photos="photos"
        :job-id="job.id"
        :upload-link="job.links.reserve_s3_photos"
        @upload-success="handlePhotosUploaded"
        @delete-success="handlePhotoDeleted"
      />
    </div>
    <FeatureFlag feature="video_uploads">
      <div class="mb-8">
        <Videos
          :videos="videos"
          :job-id="job.id"
          @upload-success="handleVideosUploaded"
          @delete-success="handleVideoDeleted"
        />
      </div>
    </FeatureFlag>
    <Docs
      :documents="documents"
      :job-id="job.id"
      :upload-link="job.links.reserve_s3_documents"
      @upload-success="handleDocumentsUploaded"
      @delete-success="handleDocumentDeleted"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import {
  MEDIA_SELECTION_KEY,
  useMediaSelection
} from "@/composables/useMediaSelection";
import Docs from "@/Documents/Documents.vue";
import Photos from "@/PhotoGallery/PhotoGallery.vue";
import { Document } from "@/typings/document";
import { Job } from "@/typings/job";
import { Photo } from "@/typings/photo";
import { Video } from "@/typings/video";
import Videos from "@/VideoGallery/VideoGallery.vue";

const store = useStore();

const job = computed<Job>(() => store.state.job);
const photos = computed<Photo[]>(() => job.value?.photos);
const documents = computed<Document[]>(() => job.value?.documents);
const videos = computed<Video[]>(() => job.value?.videos);

// One selection shared across all three galleries (each registers itself).
const selection = useMediaSelection(() => job.value?.id ?? null);
provide(MEDIA_SELECTION_KEY, selection);
const { count, selectAll, clear, deselect, applyVisibility } = selection;

async function onApply(value: boolean) {
  try {
    await applyVisibility(value);
  } catch {
    window.notify?.error("Failed to update visibility. Please try again.");
  }
}

function handlePhotosUploaded() {
  store.dispatch("fetchJob", { jobId: job.value.id });
}

function handlePhotoDeleted(photoId) {
  deselect("photo", photoId);
  job.value.photos = photos.value.filter((photo) => {
    return photo.id !== photoId;
  });
}

function handleDocumentsUploaded(uploadedDocs) {
  job.value.documents = documents.value.concat(uploadedDocs);
}

function handleDocumentDeleted(documentId) {
  deselect("document", documentId);
  job.value.documents = documents.value.filter((document) => {
    return document.id !== documentId;
  });
}

function handleVideosUploaded() {
  store.dispatch("fetchJob", { jobId: job.value.id });
}

function handleVideoDeleted(videoId) {
  deselect("video", videoId);
  job.value.videos = videos.value.filter((video) => {
    return video.id !== videoId;
  });
}
</script>

<style scoped>
.bottom-separator {
  @apply text-xs opacity-50;
  color: var(--sera-color-neutral-gray-med-gray-600);
}

/* The bar only mounts once something is selected, so it carries the active
   (blue) treatment by default rather than toggling between idle/active. */
.bulk-bar {
  @apply flex flex-wrap items-center justify-between gap-x-4 gap-y-2;
  @apply mb-4 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50;
}

.bulk-bar__info {
  @apply flex items-center gap-3 min-w-0;
}

.bulk-bar__check {
  @apply flex items-center justify-center w-5 h-5 rounded-full;
  @apply border border-blue-600 bg-blue-600 text-white text-[10px];
}

.bulk-bar__count {
  @apply text-sm font-semibold text-blue-900 whitespace-nowrap;
}

.bulk-bar__divider {
  @apply w-px h-4 bg-blue-200;
}

.bulk-bar__link {
  @apply text-sm font-medium text-blue-700 whitespace-nowrap transition-colors;
}

.bulk-bar__link:hover {
  @apply text-blue-900 underline;
}

.bulk-bar__actions {
  /* Stack full-width on phones so the actions never overflow a narrow bar;
     restore the matched-width row from sm up. */
  @apply flex flex-col w-full gap-2 sm:flex-row sm:w-auto sm:items-center;
}

.bulk-bar-enter-active,
.bulk-bar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.bulk-bar-enter-from,
.bulk-bar-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
